import {
  onCLS,
  onINP,
  onLCP,
  onTTFB,
  MetricWithAttribution,
} from 'web-vitals/attribution';
import { currentUser } from 'js/utils/user';
import routes from 'js/core_pre_webpack/routes';
import { keptAlivePost } from './kept_alive_post';
import reportOnNavigateAway from 'js/utils/reportOnNavigateAway';

declare global {
  interface Window {
    // set in show_liswp_ds.html.slim
    cwvAbTestBucket?: string;
  }
}

type BundleData = {
  firstBundleStartTime: number;
  lastBundleEndTime: number;
  bundleTimings: {
    name: string;
    duration: number;
    startTime: number;
    transferSize: number;
  }[];
};

type ServerTimingEntry = {
  name: string;
  duration: number;
  description?: string;
};

type CwvData = {
  data: {
    name: MetricWithAttribution['name'];
    value: number;
  }[];
  bundleData: BundleData;
  loggedOut: boolean;
  page: string;
  lcpElement?: string;
  lcpElementRenderDelay?: number;
  lcpResourceLoadDelay?: number;
  lcpResourceLoadDuration?: number;
  ttfbConnectDuration?: number;
  ttfbRequestDuration?: number;
  abTestBucket?: string;
  serverTiming?: ServerTimingEntry[];
};

function getMaxMetric<T extends MetricWithAttribution['name']>(
  metrics: MetricWithAttribution[],
  name: T,
): Extract<MetricWithAttribution, { name: T }> | undefined {
  const filteredMetrics = metrics.filter(
    (m): m is Extract<MetricWithAttribution, { name: T }> => m.name === name,
  );

  if (filteredMetrics.length === 0) return undefined;

  return filteredMetrics.reduce((max, current) => {
    if (current.value > max.value) {
      return current;
    }
    return max;
  });
}

function getServerTimingData(): ServerTimingEntry[] {
  const serverTimingEntries: ServerTimingEntry[] = [];

  const navigationEntries = window.performance.getEntriesByType(
    'navigation',
  ) as PerformanceNavigationTiming[];

  navigationEntries.forEach((navEntry) => {
    if (navEntry.serverTiming) {
      navEntry.serverTiming.forEach((timing) => {
        serverTimingEntries.push({
          name: timing.name,
          duration: timing.duration || 0,
        });
      });
    }
  });

  return serverTimingEntries;
}

// Match resource names of the form
// "https://www.devdemia.com:3500/webpack_bundles/js/academia-45647897123140567051.js"
// and extract the bundle name ('academia' in this case):
const BUNDLE_NAME_REGEX =
  /\/webpack_bundles\/js\/([\w_-]+)(\.wjs)?-[0-9a-f]{20}\.js$/;

function getBundleData(): BundleData {
  const resourceData = window.performance.getEntriesByType('resource');

  const bundleResourceData = resourceData
    .map((resource) => ({
      ...resource.toJSON(),
      bundleName: BUNDLE_NAME_REGEX.exec(resource.name)?.[1],
    }))
    .filter(
      // Filter out anything without a matched bundle name
      (
        resource,
      ): resource is PerformanceResourceTiming & { bundleName: string } =>
        typeof resource.bundleName === 'string',
    );

  const firstBundleStartTime = bundleResourceData[0]?.startTime;
  const lastBundleEndTime =
    bundleResourceData[bundleResourceData.length - 1]?.responseEnd;

  const bundleTimings = bundleResourceData.map((resourceData) => ({
    name: resourceData.bundleName,
    duration: resourceData.duration,
    startTime: resourceData.startTime,
    transferSize: resourceData.transferSize,
  }));

  return {
    firstBundleStartTime,
    lastBundleEndTime,
    bundleTimings,
  };
}

/**
 * This function enables tracking Core Web Vitals for any page.
 * https://web.dev/vitals/
 *
 * Just call trackCWV at the top of the webpack bundle entry for the page:
 *
 * import { trackCWV } from 'js/utils/trackCWV';
 * trackCWV();
 *
 * And you're set!
 * Users' CWV metrics will be reported to DataDog.
 * Note: this is only necessary for pages not already loading the core webpack bundle
 * or any other bundle that already calls trackCWV
 *
 * @param abTestBucket - Sets the "ab_test_bucket" tag in datadog when the given bucket
 *   is in ALLOWED_CWV_AB_TEST_BUCKETS. If not given, "ab_test_bucket" is set via
 *   window.cwvAbTestBucket if it exists.
 *   Be careful using this feature.
 *   It can quickly explode the number of custom tags
 *   we send to DataDog (which costs money).
 *   If you need help using it, reach out to frontend guild.
 *   The string should be formatted like "test_name:bucket_name".
 */
export const trackCWV = (abTestBucket?: string) => {
  const CWV_ROUTE = routes.api_v0_record_cwv_path({ subdomain_param: 'api' });

  const queue = new Set<MetricWithAttribution>();

  const addToQueue = (metric: MetricWithAttribution) => {
    queue.add(metric);
  };

  // When a user first loads a page, they may or may not be logged in.
  // In the course of interacting with a logged-out page, they may log in.
  // This will start a page reload or navigation, causing the metrics to send.
  // The navigation happens after they successfully log in, though,
  // so the CWV endpoint will get a non-nil `current_user`.
  // We want to record the metrics they send as logged out,
  // since they were logged out when they were interacting with the page.
  // This lets us override that.
  // Note that this only works in one direction -
  // we can tell the CWV endpoint that a logged-in user is logged-out,
  // but not vice versa.
  // Using `window.Aedu.User` doesn't work
  const loggedOut = !currentUser;

  const report = () => {
    if (queue.size === 0) return;

    const metrics = Array.from(queue);

    const data: CwvData = {
      data: metrics.map(({ name, value }) => ({ name, value })),
      bundleData: getBundleData(),
      loggedOut,
      page: `${window.$controller_name}:${window.$action_name}`,
    };

    // Log the element responsible for the LCP
    const maxLcpMetric = getMaxMetric(metrics, 'LCP');

    data['lcpElementRenderDelay'] =
      maxLcpMetric?.attribution?.elementRenderDelay;
    data['lcpResourceLoadDelay'] = maxLcpMetric?.attribution?.resourceLoadDelay;
    data['lcpResourceLoadDuration'] =
      maxLcpMetric?.attribution?.resourceLoadDuration;
    data['lcpElement'] = maxLcpMetric?.attribution?.target;

    // Log TTFB connect and request durations
    const maxTtfbMetric = getMaxMetric(metrics, 'TTFB');
    data['ttfbConnectDuration'] =
      maxTtfbMetric?.attribution?.connectionDuration;
    data['ttfbRequestDuration'] = maxTtfbMetric?.attribution?.requestDuration;

    if (abTestBucket != null) {
      data['abTestBucket'] = abTestBucket;
    } else if (window.cwvAbTestBucket != null) {
      data['abTestBucket'] = window.cwvAbTestBucket;
    }

    // Add server timing data if available
    const serverTimingData = getServerTimingData();
    if (serverTimingData.length > 0) {
      data['serverTiming'] = serverTimingData;
    }

    keptAlivePost(CWV_ROUTE, data);
  };

  onCLS(addToQueue);
  onLCP(addToQueue);
  onTTFB(addToQueue);
  onINP(addToQueue);

  // https://github.com/GoogleChrome/web-vitals#batch-multiple-reports-together
  reportOnNavigateAway(report);
};

let lcpFinalizedPromise: Promise<void> | null = null;

/**
 * Enables deferring some logic (usually rendering something that might affect
 * LCP) until after LCP is finalized due to user interaction.
 *
 * @returns A promise that resolves when LCP is finalized.
 */
export const lcpFinalized = (): Promise<void> => {
  if (lcpFinalizedPromise) {
    return lcpFinalizedPromise;
  }

  lcpFinalizedPromise = new Promise((resolve) => {
    // Resolve when LCP is finalized
    onLCP(() => {
      resolve();
    });

    // Also resolve when the user first scrolls - LCP is finalized upon scroll,
    // but onLCP from web-vitals does NOT fire in this case, because it can be
    // inadvertently fired by programmatic scrolling. We do not do programmatic
    // scrolling, so this is safe and still represents true LCP finalization.
    //
    // Source for web-vitals issue:
    // - https://github.com/GoogleChrome/web-vitals/issues/75
    // - https://github.com/GoogleChrome/web-vitals/blob/18528515eb75e58a94477cb000a800d9eca7c3d9/src/onLCP.ts#L114
    //
    // Source for Chrome instrumentation accounting for scrolls:
    // - https://github.com/chromium/chromium/blob/main/docs/speed/metrics_changelog/2020_05_lcp.md
    const handleScroll = () => {
      resolve();
      window.removeEventListener('scroll', handleScroll);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  });

  return lcpFinalizedPromise;
};
